import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ShieldCheck, Clock, Users, Database, CalendarCheck, Headphones, ArrowRight } from "lucide-react";

const reveal = (i = 0) => ({ initial: { opacity: 0, y: 14 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.45, delay: i * 0.08 } });

const RESULTS = [
  { icon: ShieldCheck, k: "Zéro double réservation", d: "Calendriers, tarifs et blocages synchronisés en temps réel avec Airbnb, Booking, Vrbo et 100+ canaux. Les tampons entre séjours sont appliqués partout." },
  { icon: Clock, k: "Des heures rendues chaque semaine", d: "Messages voyageurs, relances de paiement, cautions, planning ménage et relevés propriétaires partent tout seuls — vous ne faites que valider." },
  { icon: Users, k: "Des propriétaires rassurés", d: "Espace dédié, relevé mensuel PDF le 5 du mois, avis et documents : la relation propriétaire devient professionnelle, sans un e-mail de plus." },
];

export function Results() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-16 sm:py-20" id="resultats" data-testid="landing-results">
      <div className="grid gap-5 md:grid-cols-3">
        {RESULTS.map((r, i) => (
          <motion.div key={r.k} {...reveal(i)} className="rounded-2xl border border-white/10 bg-white/[0.03] p-7" data-testid={`landing-result-${i}`}>
            <r.icon className="h-6 w-6 text-brass" />
            <p className="display text-xl text-white mt-4">{r.k}</p>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">{r.d}</p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}

const CALLOUTS = [
  { x: "27%", y: "17%", t: "À faire aujourd'hui : départs, ménages, paiements en retard — en un coup d'œil" },
  { x: "27%", y: "44%", t: "Alertes saisonnières et annulations groupées, regroupées et cliquables" },
  { x: "27%", y: "72%", t: "CA du mois, occupation, solde en attente : comparés à l'an dernier" },
  { x: "5%", y: "44%", t: "JustPrice : tarification dynamique nuit par nuit (marché Airbnb réel)" },
];

export function ProductShot() {
  return (
    <section className="max-w-6xl mx-auto px-6 sm:px-10 py-12 sm:py-16" id="application" data-testid="landing-product-shot">
      <p className="text-xs uppercase tracking-[0.2em] text-brass">Le vrai logiciel, pas une maquette</p>
      <h2 className="display mt-4 text-3xl sm:text-4xl text-white max-w-2xl">Votre matinée tient sur un écran.</h2>
      <motion.div {...reveal(1)} className="relative mt-10 rounded-2xl border border-white/10 bg-[#0b1a35] shadow-2xl shadow-black/40 overflow-hidden">
        <img src="/screens/dashboard-2026.webp" srcSet="/screens/dashboard-2026-720.webp 720w, /screens/dashboard-2026.webp 1440w" sizes="(max-width: 768px) 100vw, 1100px" width="1440" height="900" alt="Tableau de bord Casanéo : actions du jour, alertes, indicateurs" className="w-full h-auto block" loading="lazy" decoding="async" data-testid="landing-product-shot-img" />
        {CALLOUTS.map((c, i) => (
          <div key={i} className="hidden md:block absolute" style={{ left: c.x, top: c.y }} data-testid={`landing-callout-${i}`}>
            <span className="relative flex h-6 w-6"><span className="absolute inline-flex h-full w-full rounded-full bg-brass opacity-60 animate-ping" /><span className="relative inline-flex h-6 w-6 rounded-full bg-brass text-[#0b1a35] text-xs font-bold items-center justify-center">{i + 1}</span></span>
          </div>
        ))}
      </motion.div>
      <ol className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-sm text-slate-300">
        {CALLOUTS.map((c, i) => <li key={i} className="flex gap-2.5"><span className="h-5 w-5 shrink-0 rounded-full bg-brass text-[#0b1a35] text-[11px] font-bold grid place-items-center">{i + 1}</span>{c.t}</li>)}
      </ol>
    </section>
  );
}

const MIGRATION = [
  { icon: Database, t: "Import de vos logements", d: "Depuis Channex, Lodgify, Smoobu ou un fichier Excel : photos, descriptions, tarifs, règles. Compter 1 h pour 20 logements, avec nous." },
  { icon: CalendarCheck, t: "Reprise des réservations", d: "Les réservations à venir et l'historique 12 mois sont récupérés via les canaux connectés ; rien n'est ressaisi, aucune coupure de calendrier." },
  { icon: Headphones, t: "Accompagnement", d: "Un appel de cadrage, un suivi Telegram pendant le démarrage, et le premier relevé propriétaire vérifié ensemble. Délai réel constaté : 3 à 5 jours ouvrés." },
  { icon: ShieldCheck, t: "Sécurité & données", d: "Hébergement en Europe, sauvegardes quotidiennes, export complet RGPD à tout moment, page de statut publique. Vos données restent les vôtres." },
];

export function Migration() {
  return (
    <section className="border-y border-white/5 bg-white/[0.02]" id="migration" data-testid="landing-migration">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
        <p className="text-xs uppercase tracking-[0.2em] text-brass">Migration & sécurité</p>
        <h2 className="display mt-4 text-3xl sm:text-4xl text-white max-w-2xl">Changer d'outil sans perdre une réservation.</h2>
        <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {MIGRATION.map((m, i) => (
            <motion.div key={m.t} {...reveal(i)} className="rounded-2xl border border-white/10 p-6" data-testid={`landing-migration-${i}`}>
              <m.icon className="h-5 w-5 text-brass" />
              <p className="text-white font-medium mt-3">{m.t}</p>
              <p className="text-sm text-slate-400 mt-2 leading-relaxed">{m.d}</p>
            </motion.div>
          ))}
        </div>
        <Link to="/onboarding" className="mt-8 inline-flex items-center gap-2 text-sm text-brass hover:underline" data-testid="landing-migration-cta">Démarrer l'essai et planifier ma migration <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </section>
  );
}
